import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarsIcon from '@mui/icons-material/Stars';
import ExtensionIcon from '@mui/icons-material/Extension';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';

const getCurrencySymbol = (currency) => {
  switch ((currency || '').toUpperCase()) {
    case 'GBP': return '£';
    case 'INR': return '₹';
    case 'EUR': return '€';
    default: return '$';
  }
};

const getStatusBadge = (status) => {
  const norm = (status || '').toLowerCase();
  switch (norm) {
    case 'active':
      return {
        label: 'Active',
        sx: { bgcolor: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontWeight: 600 }
      };
    case 'trialing':
      return {
        label: 'Trialing',
        sx: { bgcolor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', fontWeight: 600 }
      };
    case 'past_due':
      return {
        label: 'Past Due',
        sx: { bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', fontWeight: 600 }
      };
    case 'canceled':
      return {
        label: 'Canceled',
        sx: { bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontWeight: 600 }
      };
    default:
      return {
        label: status ? status.toUpperCase() : 'No Plan',
        sx: { bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', fontWeight: 600 }
      };
  }
};

const PlanSubscriptionCard = ({ contacts = {}, saasPlans = [], planModules = [] }) => {
  const rawPlan = (contacts.subscribedPlan || '').toLowerCase().replace(/[\s_-]*plan$/i, '').trim();
  const matchedPlan = saasPlans.find(
    (p) => p.slug === rawPlan || p.name?.toLowerCase() === rawPlan
  );

  const planName = matchedPlan?.name || contacts.subscribedPlan || 'Not Subscribed';
  const currency = contacts.currency || contacts.planCustomization?.currency || 'GBP';
  const currSymbol = getCurrencySymbol(currency);
  const billingCycle = contacts.billingCycle || contacts.planCustomization?.billingCycle || 'monthly';
  const statusBadge = getStatusBadge(contacts.stripeSubscriptionStatus || (contacts.subscribedPlan ? 'active' : ''));

  // Calculate pricing
  const basePlanPrice =
    matchedPlan?.prices?.[billingCycle]?.[currency] ||
    matchedPlan?.prices?.['monthly']?.[currency] ||
    0;

  const activeAddOns = contacts.activeAddOns || [];
  const addOnsCost = activeAddOns.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
  const totalMonthlyCost = basePlanPrice + addOnsCost;

  // Included modules
  const includedModuleSlugs = matchedPlan?.includedModules || [];
  const includedModules = includedModuleSlugs.map((slug) => {
    const mod = planModules.find((m) => m.slug === slug);
    return {
      slug,
      name: mod?.name || slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      description: mod?.description || '',
    };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* ── 1. Plan Overview Card ── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        {/* Header Ribbon */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            color: 'white',
            p: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarsIcon sx={{ color: '#38bdf8', fontSize: 22 }} />
              <Typography variant="subtitle2" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem', fontWeight: 700 }}>
                Customer Subscription
              </Typography>
            </Box>
            <Chip size="small" label={statusBadge.label} sx={statusBadge.sx} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc', mb: 0.5 }}>
            {planName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
              {currSymbol}{totalMonthlyCost}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              /{billingCycle === 'annual' ? 'year' : 'month'}
            </Typography>
          </Box>

          {activeAddOns.length > 0 && (
            <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block', mt: 0.5 }}>
              Includes {currSymbol}{basePlanPrice} base plan + {currSymbol}{addOnsCost} in add-on modules
            </Typography>
          )}
        </Box>

        {/* Subscription Meta Details */}
        <CardContent sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                Billing Cycle
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', textTransform: 'capitalize' }}>
                {billingCycle}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                Currency
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                {currency} ({currSymbol})
              </Typography>
            </Grid>

            {contacts.stripeSubscriptionId && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Stripe Subscription ID
                </Typography>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    bgcolor: '#f8fafc',
                    p: 1,
                    borderRadius: 1.5,
                    border: '1px solid #e2e8f0',
                    color: '#334155',
                    wordBreak: 'break-all',
                  }}
                >
                  {contacts.stripeSubscriptionId}
                </Box>
              </Grid>
            )}

            {contacts.stripeSubscriptionCurrentPeriodEnd && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#475569' }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
                  <Typography variant="caption">
                    Current period renewal:{' '}
                    <strong>
                      {dayjs(contacts.stripeSubscriptionCurrentPeriodEnd).format('DD MMM YYYY')}
                    </strong>
                  </Typography>
                </Box>
              </Grid>
            )}

            {contacts.stripeSubscriptionTrialEnd && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#6d28d9' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    Trial ends:{' '}
                    <strong>
                      {dayjs(contacts.stripeSubscriptionTrialEnd).format('DD MMM YYYY')}
                    </strong>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* ── 2. Opted-In Add-on Modules Card ── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExtensionIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Opted-In Add-on Modules
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${activeAddOns.length} Active`}
            color={activeAddOns.length > 0 ? 'primary' : 'default'}
            sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }}
          />
        </Box>

        <Divider />

        <CardContent sx={{ p: 2.5 }}>
          {activeAddOns.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                No extra add-on modules currently opted in.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activeAddOns.map((addon, index) => {
                const mod = planModules.find((m) => m.slug === addon.moduleSlug);
                const name = addon.moduleName || mod?.name || addon.moduleSlug;
                const addonCurrSymbol = getCurrencySymbol(addon.currency || currency);

                return (
                  <Box
                    key={addon.moduleSlug || index}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #ede9fe',
                      bgcolor: '#faf5ff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4c1d95' }}>
                          {name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7c3aed' }}>
                          Module Slug: <code>{addon.moduleSlug}</code>
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label="Add-on"
                        sx={{ bgcolor: '#8b5cf6', color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>

                    <Divider sx={{ borderColor: '#ede9fe' }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6d28d9', display: 'block' }}>
                          Price
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {addonCurrSymbol}{addon.price || 0}
                          <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#64748b' }}>
                            /{addon.billingCycle || 'mo'}
                          </span>
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#6d28d9', display: 'block' }}>
                          Activated On
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {addon.activatedAt ? dayjs(addon.activatedAt).format('DD MMM YYYY') : 'Recently'}
                        </Typography>
                      </Grid>

                      {addon.stripeItemId && (
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#6d28d9', display: 'block' }}>
                            Stripe Item ID
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#475569', display: 'block' }}>
                            {addon.stripeItemId}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── 3. Base Plan Included Modules Card ── */}
      {includedModules.length > 0 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Included with {planName}
            </Typography>
          </Box>

          <Divider />

          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {includedModules.map((mod) => (
                <Box
                  key={mod.slug}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                      {mod.name}
                    </Typography>
                    {mod.description && (
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                        {mod.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── 4. Custom Resource Limits (if any) ── */}
      {contacts.planCustomization?.options && Object.keys(contacts.planCustomization.options).length > 0 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Customized Resource Limits
            </Typography>
          </Box>

          <Divider />

          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(contacts.planCustomization.options).map(([key, val]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#475569', textTransform: 'capitalize' }}>
                    {key.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {typeof val === 'number' ? val.toLocaleString() : String(val)}
                  </Typography>
                </Box>
              ))}

              {contacts.planCustomization.extraMonthlyCost > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600 }}>
                    Extra Monthly Surcharge
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700 }}>
                    +{currSymbol}{contacts.planCustomization.extraMonthlyCost}/mo
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PlanSubscriptionCard;